package com.evoapps.evoshift.ui.roster

import androidx.compose.runtime.key
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.roster_open_count
import evoshift.composeapp.generated.resources.roster_open_short
import evoshift.composeapp.generated.resources.roster_unpublished_shift
import evoshift.composeapp.generated.resources.roster_unfilled_count
import evoshift.composeapp.generated.resources.roster_unfilled_short
import com.evoapps.evoshift.ui.theme.motionContentSize
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.domain.model.DayStaffAlert
import com.evoapps.evoshift.domain.model.ShiftLeadRule
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.ui.user.ExpiringAmber
import com.evoapps.evoshift.ui.user.StaffBadges
import com.evoapps.evoshift.ui.user.text
import androidx.compose.material.icons.filled.SupervisorAccount
import com.evoapps.evoshift.domain.legal.DayClosure
import com.evoapps.evoshift.ui.util.closureLabel
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.util.dayShort
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ChevronRight
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.staffing.DayStaffingHint
import com.evoapps.evoshift.ui.util.StaffingChip
import com.evoapps.evoshift.ui.DeepNavy
import kotlinx.datetime.DayOfWeek
import com.evoapps.evoshift.domain.model.Interview
import evoshift.composeapp.generated.resources.interview_title
import androidx.compose.foundation.border
import androidx.compose.ui.platform.testTag
import kotlinx.datetime.LocalDate
import com.evoapps.evoshift.domain.model.DayWeather
import com.evoapps.evoshift.ui.util.WeatherAttribution
import com.evoapps.evoshift.ui.util.WeatherBadge
import androidx.compose.material3.LocalContentColor
import androidx.compose.runtime.CompositionLocalProvider
import com.evoapps.evoshift.domain.model.DaySpecials
import com.evoapps.evoshift.ui.util.SpecialDayMarker
import androidx.compose.foundation.combinedClickable
import evoshift.composeapp.generated.resources.local_event_add

/** SH-227: a day's row in the phone's day list (holding it adds an event). */
fun compactDayTag(date: LocalDate) = "compact_day_$date"

private val OpenRed = Color(0xFFD32F2F)
private val AssignedBeige = Color(0xFFFFF4DC)

/**
 * Phone layout of the roster builder: one row per day with its open demand and the people on it.
 * The employee x day grid needs a tablet or a desktop; here a tap opens the day's sheet, where
 * every shift can be edited or assigned.
 */
@Composable
fun CompactRosterDayList(
    dates: List<LocalDate>,
    shiftsByDate: Map<LocalDate, List<Shift>>,
    closedDays: Map<LocalDate, DayClosure> = emptyMap(),
    onDayClick: (LocalDate) -> Unit,
    modifier: Modifier = Modifier,
    /** SH-47: days without a shift lead or with only people who need supervision. */
    dayWarnings: Map<LocalDate, DayStaffAlert> = emptyMap(),
    /** SH-47: a person's engagement here, for the shift-lead star by their name. */
    engagementOf: (String) -> Engagement? = { null },
    /** SH-104: who leads shifts here, for the star. */
    leadRule: ShiftLeadRule = ShiftLeadRule(),
    /** SH-54: each day's staffing hint, for those who may see money. */
    staffing: Map<LocalDate, DayStaffingHint> = emptyMap(),
    /** SH-102: the foldable's week pager scrolls it. */
    state: LazyListState = rememberLazyListState(),
    /** SH-210: each day's job interviews with who takes part (names), never counted as shifts. */
    interviewsByDate: Map<LocalDate, List<Pair<Interview, String>>> = emptyMap(),
    /** SH-226: the forecast for today and the coming days (empty with weather off). */
    weather: Map<LocalDate, DayWeather> = emptyMap(),
    /** SH-227: each day's events and special days (one small marker). */
    specialDays: Map<LocalDate, DaySpecials> = emptyMap(),
    /** SH-227: holding a day adds an event (owners and managers; null: not offered). */
    onDayLongClick: ((LocalDate) -> Unit)? = null
) {
    LazyColumn(modifier = modifier, state = state) {
        items(dates, key = { it.toString() }) { date ->
            DayRow(
                date, shiftsByDate[date].orEmpty(), closedDays[date], dayWarnings[date], engagementOf, leadRule, staffing[date],
                interviews = interviewsByDate[date].orEmpty(), weather = weather[date], specials = specialDays[date],
                onClick = { onDayClick(date) }, onLongClick = onDayLongClick?.let { { it(date) } }
            )
            HorizontalDivider(color = DeepNavy.copy(alpha = 0.08f))
        }
        // SH-226: the forecast's credit, under the days it is shown on.
        if (dates.any { it in weather }) {
            item(key = "weather-attribution") {
                CompositionLocalProvider(LocalContentColor provides DeepNavy.copy(alpha = 0.75f)) {
                    WeatherAttribution(DayWeather.sourcesOf(dates.map { weather[it] }), Modifier.padding(horizontal = 16.dp, vertical = 8.dp))
                }
            }
        }
        item { Spacer(Modifier.height(80.dp)) }
    }
}

@Composable
private fun DayRow(
    date: LocalDate,
    shifts: List<Shift>,
    closure: DayClosure?,
    warning: DayStaffAlert?,
    engagementOf: (String) -> Engagement?,
    leadRule: ShiftLeadRule,
    staffing: DayStaffingHint?,
    interviews: List<Pair<Interview, String>> = emptyList(),
    /** SH-226: the day's forecast (today and the coming days only). */
    weather: DayWeather? = null,
    /** SH-227: the day's events and special days. */
    specials: DaySpecials? = null,
    onClick: () -> Unit,
    onLongClick: (() -> Unit)? = null
) {
    val unfilled = shifts.count { it.userId == null && !it.isOpen }
    val open = shifts.count { it.userId == null && it.isOpen }
    val assigned = shifts.filter { it.userId != null }.sortedBy { it.plannedTime.start }
    val weekend = date.dayOfWeek == DayOfWeek.SATURDAY || date.dayOfWeek == DayOfWeek.SUNDAY
    // SH-263: "Show" on the unpublished-changes banner tints the days with changes and rings their shifts.
    val highlighted = LocalUnpublishedHighlight.current
    val changed = highlighted.isNotEmpty() && shifts.any { it.id in highlighted }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .then(if (changed) Modifier.background(UnpublishedTint) else Modifier)
            .testTag(compactDayTag(date))
            .handCursor()
            // SH-227: holding the day adds an event (or opens the day's events).
            .combinedClickable(onLongClick = onLongClick, onLongClickLabel = if (onLongClick != null) stringResource(Res.string.local_event_add) else null, onClick = onClick)
            .padding(horizontal = 16.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(modifier = Modifier.width(52.dp), horizontalAlignment = Alignment.CenterHorizontally) {
            Text(
                dayShort(date.dayOfWeek).uppercase(),
                style = MaterialTheme.typography.labelSmall,
                color = if (weekend) OpenRed else DeepNavy.copy(alpha = 0.6f),
                fontWeight = FontWeight.Bold
            )
            Text(
                date.day.toString().padStart(2, '0'),
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                color = DeepNavy
            )
            if (weather != null) {
                CompositionLocalProvider(LocalContentColor provides DeepNavy.copy(alpha = 0.75f)) {
                    WeatherBadge(weather, iconSize = 14.dp)
                }
            }
            if (changed) {
                val waiting = stringResource(Res.string.roster_unpublished_shift)
                Box(
                    Modifier.padding(top = 4.dp).size(8.dp).clip(RoundedCornerShape(4.dp)).background(UnpublishedEdge)
                        .testTag(UnpublishedTags.day(date)).clearAndSetSemantics { contentDescription = waiting }
                )
            }
        }
        Spacer(Modifier.width(12.dp))
        // SH-158: a shift added or removed slides the day's lines instead of jumping.
        Column(modifier = Modifier.weight(1f).motionContentSize(), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            // SH-227: the day's event or special day, one small line above its shifts.
            if (specials != null) {
                CompositionLocalProvider(LocalContentColor provides DeepNavy.copy(alpha = 0.75f)) {
                    SpecialDayMarker(specials, iconSize = 14.dp)
                }
            }
            // SH-55: "Closed (non-trading Sunday)" and the like.
            if (closure != null) {
                Text(closureLabel(closure), color = DeepNavy.copy(alpha = 0.6f), style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.SemiBold)
            } else if (shifts.isEmpty() && interviews.isEmpty()) {
                Text("—", color = DeepNavy.copy(alpha = 0.4f), style = MaterialTheme.typography.bodyMedium)
            }
            // SH-210: interviews, violet like in the grid.
            interviews.forEach { (interview, names) ->
                key("interview-${interview.id}") {
                    Row(
                        modifier = Modifier.clip(RoundedCornerShape(6.dp)).background(InterviewTint)
                            .border(1.dp, InterviewInk.copy(alpha = 0.55f), RoundedCornerShape(6.dp))
                            .padding(horizontal = 8.dp, vertical = 3.dp)
                            .testTag(InterviewGridTags.cell(interview.id)),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text("${interview.startTime}–${interview.endTime}", style = MaterialTheme.typography.labelMedium, color = InterviewInk, fontWeight = FontWeight.Bold)
                        Spacer(Modifier.width(8.dp))
                        Text(
                            "${stringResource(Res.string.interview_title)}: $names", style = MaterialTheme.typography.labelMedium,
                            color = InterviewInk, maxLines = 1, overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis
                        )
                    }
                }
            }
            assigned.take(3).forEach { shift ->
                key(shift.id) { ShiftLine(shift, shift.userId?.let(engagementOf), leadRule, changed = shift.id in highlighted) }
            }
            if (warning != null) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.SupervisorAccount, null, tint = ExpiringAmber, modifier = Modifier.size(14.dp))
                    Spacer(Modifier.width(4.dp))
                    Text(warning.text(), style = MaterialTheme.typography.labelSmall, color = ExpiringAmber, fontWeight = FontWeight.Bold)
                }
            }
            if (assigned.size > 3) {
                Text("+${assigned.size - 3}", style = MaterialTheme.typography.labelSmall, color = DeepNavy.copy(alpha = 0.6f))
            }
            // SH-54: too few / just right / too many for the day's expected sales.
            if (staffing != null) StaffingChip(staffing)
        }
        // Gold: open shifts offered to the team; red: unfilled shifts publishing would remove.
        // SH-163: labelled like the month's figures and the tablet's day headers ("Nieobsadzone: 2"),
        // read out as "2 nieobsadzone zmiany".
        Column(horizontalAlignment = Alignment.End, verticalArrangement = Arrangement.spacedBy(4.dp)) {
            if (unfilled > 0) {
                CountBadge(
                    stringResource(Res.string.roster_unfilled_short, unfilled),
                    pluralStringResource(Res.plurals.roster_unfilled_count, unfilled, unfilled),
                    OpenRed
                )
            }
            if (open > 0) {
                CountBadge(
                    stringResource(Res.string.roster_open_short, open),
                    pluralStringResource(Res.plurals.roster_open_count, open, open),
                    DarkGold
                )
            }
        }
        Icon(Icons.Default.ChevronRight, contentDescription = null, tint = DeepNavy.copy(alpha = 0.5f))
    }
}

@Composable
private fun ShiftLine(shift: Shift, engagement: Engagement?, leadRule: ShiftLeadRule, changed: Boolean = false) {
    val start = shift.plannedTime.start.substringAfter('T').take(5)
    val end = shift.plannedTime.end.substringAfter('T').take(5)
    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(6.dp))
            .background(AssignedBeige)
            .then(if (changed) Modifier.border(2.dp, UnpublishedEdge, RoundedCornerShape(6.dp)).testTag(UnpublishedTags.shift(shift.id)) else Modifier)
            .padding(horizontal = 8.dp, vertical = 3.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text("$start–$end", style = MaterialTheme.typography.labelMedium, color = DeepNavy, fontWeight = FontWeight.Bold)
        Spacer(Modifier.width(8.dp))
        Text(
            if (shift.userId != null) com.evoapps.evoshift.ui.util.personName(shift.userDisplayName) else shift.userDisplayName.orEmpty(),
            style = MaterialTheme.typography.labelMedium,
            color = DeepNavy.copy(alpha = 0.8f),
            maxLines = 1
        )
        StaffBadges(engagement, leadRule, shift.userId, size = 12.dp, modifier = Modifier.padding(start = 4.dp))
    }
}

/** SH-163: a day's count with its label ("Wolne: 1"), described for screen readers as [description]. */
@Composable
private fun CountBadge(label: String, description: String, color: Color) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(color)
            .padding(horizontal = 8.dp, vertical = 4.dp)
            .clearAndSetSemantics { contentDescription = description }
    ) {
        Text(label, color = Color.White, style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold, maxLines = 1)
    }
}
